import React from "react";
import PropTypes from "prop-types";
import clickOutsideDetector from "react-onclickoutside";
import classnames from "classnames";
import { escapeRegexp } from "services/strings";
import SvgIcon from "components/Common/SvgIcon";
import InputSearch from "components/Common/InputSearch";

import "./style.scss";

const BLOCK_NAME = "SelectSearchDropdown";
class SelectSearchDropdown extends React.Component {
  /**
   * @param {Object} props
   */
  constructor(props) {
    super(props);

    this.state = {
      searchQuery: "",
      listOpen: false,
      inputSearchFocus: false
    };

    this.handleToggleList = this.handleToggleList.bind(this);
    this.handleInputSearchChange = this.handleInputSearchChange.bind(this);
    this.handleInputSearchFocus = this.handleInputSearchFocus.bind(this);
  }

  /**
   * find the option object that contains the selected value
   * value can exist in options or topOptions
   * @returns {Object|null}
   */
  findOptionByValue() {
    const { value, options, topOptions } = this.props;

    const selectedOption = options.find(option => option.value === value);
    const selectedTopOption = topOptions.find(topOption => topOption.value === value);

    if (selectedOption) {
      return selectedOption;
    }

    if (selectedTopOption) {
      return selectedTopOption;
    }

    return null;
  }

  /**
   * Show or hide the dropdown menu
   */
  handleToggleList() {
    this.setState(prevState => ({
      listOpen: !prevState.listOpen,
      inputSearchFocus: false,
      searchQuery: ""
    }));
  }

  /**
   * Keep track of input search bar focus event
   */
  handleInputSearchFocus() {
    this.setState(prevState => ({
      inputSearchFocus: !prevState.inputSearchFocus
    }));
  }

  /**
   * @param {Object} e
   */
  handleInputSearchChange(e) {
    this.setState({ searchQuery: e.target.value });
  }

  /**
   * @param {String} value - option value
   */
  handleItemSelect(value) {
    const { onChange } = this.props;

    onChange(value);
    this.handleToggleList();
  }

  /**
   * This is a function injected by the "clickOutsideDetector" wrapper component
   */
  handleClickOutside() {
    this.setState({
      listOpen: false,
      inputSearchFocus: false,
      searchQuery: ""
    });
  }

  /**
   * @returns {ReactElement}
   */
  render() {
    const { searchQuery, listOpen, inputSearchFocus } = this.state;
    const {
      topOptions, options, placeholder, placeholderIcon, topOptionsLimit
    } = this.props;

    const selectedOption = this.findOptionByValue();
    const limitedTopOptions = topOptionsLimit ? topOptions.slice(0, topOptionsLimit) : topOptions;
    const serachRegexp = new RegExp(escapeRegexp(searchQuery), "i");
    const filteredOptions = options.filter(option => serachRegexp.test(option.label));

    return (
      <div
        className={classnames(
          BLOCK_NAME, {
            [`${BLOCK_NAME}--open`]: listOpen
          }
        )}
      >
        <button
          type="button"
          className={classnames(
            `${BLOCK_NAME}__header`, {
              [`${BLOCK_NAME}__header--active`]: !inputSearchFocus
            }
          )}
          onClick={this.handleToggleList}
        >
          <div className={`${BLOCK_NAME}__header-title-container`}>
            {placeholderIcon ? (
              <div className={`${BLOCK_NAME}__header-icon`}>
                <SvgIcon icon={placeholderIcon} height={24} />
              </div>
            ) : null}
            <div className={`${BLOCK_NAME}__header-title`}>
              {selectedOption ? selectedOption.label : placeholder}
            </div>
            <div className={`${BLOCK_NAME}__header-arrow`}>
              <SvgIcon icon="ChevronDown" height={24} />
            </div>
          </div>
        </button>

        {listOpen && (
          <div className={`${BLOCK_NAME}__dropdown-container`}>
            <div className={`${BLOCK_NAME}__search-block`}>
              <InputSearch
                customClassName={`${BLOCK_NAME}__search-block-input`}
                value={searchQuery}
                onChange={this.handleInputSearchChange}
                onFocus={this.handleInputSearchFocus}
                placeholder="Type of Search"
                hideChevron
              />
            </div>

            {filteredOptions.length > 0
              ? (
                <ul className={`${BLOCK_NAME}__list-group`}>
                  {limitedTopOptions.length > 0 && searchQuery === "" && (
                    <>
                      {limitedTopOptions.map(topOption => (
                        <li
                          className={`${BLOCK_NAME}__list-item-container`}
                          key={topOption.value}
                        >
                          <button
                            type="button"
                            className={classnames(
                              `${BLOCK_NAME}__list-item`, {
                                [`${BLOCK_NAME}__list-item--active`]: selectedOption && selectedOption.value === topOption.value
                              }
                            )}
                            onClick={() => this.handleItemSelect(topOption.value)}
                          >
                            {topOption.label}
                            <div className={`${BLOCK_NAME}__list-item-selected-icon`}>
                              {selectedOption && selectedOption.value === topOption.value && (
                                <SvgIcon
                                  icon="Checkmark"
                                  height={20}
                                  width={20}
                                />
                              )}
                            </div>
                          </button>
                        </li>
                      ))}
                      <li className={`${BLOCK_NAME}__list-item-container`}>
                        <div className={`${BLOCK_NAME}__list-separator`} />
                      </li>
                    </>
                  )}
                  {filteredOptions.map(filteredOption => (
                    <li
                      className={`${BLOCK_NAME}__list-item-container`}
                      key={filteredOption.value}
                    >
                      <button
                        type="button"
                        className={classnames(
                          `${BLOCK_NAME}__list-item`, {
                            [`${BLOCK_NAME}__list-item--active`]: selectedOption && selectedOption.value === filteredOption.value
                          }
                        )}
                        onClick={() => this.handleItemSelect(filteredOption.value)}
                      >
                        {filteredOption.label}
                        <div className={`${BLOCK_NAME}__list-item-selected-icon`}>
                          {selectedOption && selectedOption.value === filteredOption.value && (
                            <SvgIcon
                              icon="Checkmark"
                              height={20}
                              width={20}
                            />
                          )}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className={`${BLOCK_NAME}__search-no-results`}>Hmmm&hellip;</div>
              )}
          </div>
        )}
      </div>
    );
  }
}

const optionsPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })
);

SelectSearchDropdown.propTypes = {
  placeholder: PropTypes.string.isRequired,
  placeholderIcon: PropTypes.string,
  topOptions: optionsPropTypes,
  options: optionsPropTypes.isRequired,
  topOptionsLimit: PropTypes.number,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired
};

SelectSearchDropdown.defaultProps = {
  value: null,
  topOptions: [],
  placeholderIcon: null,
  topOptionsLimit: 3
};

const HOC = clickOutsideDetector(SelectSearchDropdown);

export default HOC;
